/**
 * The app shell (ADR-0048: loaded lazily, so the landing's first bundle carries none of it). Anonymous visitors see the
 * auth pages; signed-in users see the cabinet, Security and (staff) the admin area. Every page is its own chunk.
 */
import type { Me } from "@prop/contracts";
import { Button, cn } from "@prop/ui";
import { ChevronDown, Monitor, Moon, ShieldCheck, Sun } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { StepUpProvider } from "../features/security/StepUp";
import { BalanceChip } from "../features/money/BalanceChip";
import { useCoachT, useMoneyT, useShellT, useSupportT, useTradeT, useWalletT } from "../i18n";
import { Logo } from "../ui/Logo";
import { LangMenu } from "./LangMenu";
import { post } from "../lib/api";
import { preloadable } from "../lib/preloadable";
import { can, useSetMe } from "../lib/session";
import { useAction } from "../lib/useAction";
import { MobileNav } from "./MobileNav";
import { needsSignIn } from "./publicRoutes";
import { THEMES, type ThemeChoice, useTheme } from "./theme";

// Route-level code splitting (ADR-0048): every page — and the chart, admin and money code behind it — loads when its
// route is opened.
const AdminPage = lazy(() => import("../features/admin/AdminPage").then((m) => ({ default: m.AdminPage })));
const BreachScreen = lazy(() => import("../features/breach/BreachScreen").then((m) => ({ default: m.BreachScreen })));
const CoachPage = lazy(() => import("../features/coach/CoachPage").then((m) => ({ default: m.CoachPage })));
const AccountDashboard = lazy(() => import("../features/cabinet/AccountDashboard").then((m) => ({ default: m.AccountDashboard })));
const CertificatePage = lazy(() => import("../features/cabinet/AccountDashboard").then((m) => ({ default: m.CertificatePage })));
const AccountsPage = lazy(() => import("../features/cabinet/AccountsPage").then((m) => ({ default: m.AccountsPage })));
const SettingsPage = lazy(() => import("../features/cabinet/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const money = () => import("../features/money/TraderMoney");
const AffiliatePage = lazy(() => money().then((m) => ({ default: m.AffiliatePage })));
const BuyPage = lazy(() => money().then((m) => ({ default: m.BuyPage })));
const CheckoutPage = lazy(() => money().then((m) => ({ default: m.CheckoutPage })));
const KycPage = lazy(() => money().then((m) => ({ default: m.KycPage })));
const OrdersPage = lazy(() => money().then((m) => ({ default: m.OrdersPage })));
const PayoutsPage = lazy(() => money().then((m) => ({ default: m.PayoutsPage })));
const BalancePage = lazy(() => import("../features/money/BalancePage").then((m) => ({ default: m.BalancePage })));
// The public pages load ahead of a render too: the prerender and the hydration of their HTML (ADR-0061).
const site = () => import("../features/site/SitePages");
const FaqPage = preloadable(() => site().then((m) => m.FaqPage));
const ProgramsPage = preloadable(() => site().then((m) => m.ProgramsPage));
const RulesPage = lazy(() => site().then((m) => ({ default: m.RulesPage })));
const LegalPage = preloadable(() => import("../features/site/LegalPage").then((m) => m.LegalPage));

/** Loads the code of the public page at `path` (none for an app path: those are not prerendered). */
export function preloadPage(path: string): Promise<unknown> {
  if (path === "/faq") return FaqPage.preload();
  if (path === "/programs") return ProgramsPage.preload();
  if (path.startsWith("/legal/")) return LegalPage.preload();
  return Promise.resolve();
}
const SupportPage = lazy(() => import("../features/support/SupportPage").then((m) => ({ default: m.SupportPage })));
const TerminalPage = lazy(() => import("../features/terminal/TerminalPage").then((m) => ({ default: m.TerminalPage })));
const auth = () => import("../features/auth/AuthPages");
const ForgotPasswordPage = lazy(() => auth().then((m) => ({ default: m.ForgotPasswordPage })));
const LoginPage = lazy(() => auth().then((m) => ({ default: m.LoginPage })));
const RegisterPage = lazy(() => auth().then((m) => ({ default: m.RegisterPage })));
const ResetPasswordPage = lazy(() => auth().then((m) => ({ default: m.ResetPasswordPage })));
const VerifyEmailPage = lazy(() => auth().then((m) => ({ default: m.VerifyEmailPage })));
const MfaPage = lazy(() => import("../features/auth/MfaPage").then((m) => ({ default: m.MfaPage })));
const SecurityPage = lazy(() => import("../features/security/SecurityPage").then((m) => ({ default: m.SecurityPage })));

const THEME_ICON: Record<ThemeChoice, typeof Sun> = { light: Sun, dark: Moon, system: Monitor };

function ThemeSwitch() {
  const t = useShellT();
  const [theme, setTheme] = useTheme();
  return (
    <fieldset className="flex items-center gap-0.5 rounded-full border border-border p-0.5" aria-label={t("theme.label")}>
      {THEMES.map((choice) => {
        const Icon = THEME_ICON[choice];
        return (
          <Button
            key={choice}
            type="button"
            size="icon-sm"
            variant={theme === choice ? "secondary" : "ghost"}
            aria-pressed={theme === choice}
            title={t(`theme.${choice}`)}
            onClick={() => setTheme(choice)}
          >
            <Icon aria-hidden />
            <span className="sr-only">{t(`theme.${choice}`)}</span>
          </Button>
        );
      })}
    </fieldset>
  );
}

function NavLink({ href, children }: { href: string; children: string }) {
  const [location] = useLocation();
  const active = href === "/" ? location === "/" : location.startsWith(href);
  return (
    <Link href={href} aria-current={active ? "page" : undefined} className={cn("rounded-full px-3 py-1.5 text-sm", active ? "bg-foreground font-medium text-background" : "text-muted-foreground hover:text-foreground")}>
      {children}
    </Link>
  );
}

/**
 * The rest of the signed-in pages behind one "More" pill (UX law 6): eleven links do not fit a 1280–1440px header in
 * Russian or Spanish, and a clipped nav hid Security and the admin. Escape, a click outside or a pick closes it.
 */
function NavMore({ label, items }: { label: string; items: Array<{ href: string; label: string }> }) {
  const [location] = useLocation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const away = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);
  // A new page closes the menu.
  useEffect(() => {
    setOpen(false);
  }, [location]);
  const active = items.some((i) => location.startsWith(i.href));
  return (
    <div ref={root} className="relative" onKeyDown={(e) => e.key === "Escape" && setOpen(false)}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className={cn("flex items-center gap-1 rounded-full px-3 py-1.5 text-sm", active ? "bg-foreground font-medium text-background" : "text-muted-foreground hover:text-foreground")}
      >
        {label}
        <ChevronDown className={cn("size-3.5 opacity-70 transition-transform duration-200", open && "rotate-180")} aria-hidden />
      </button>
      {open && (
        <div role="menu" aria-label={label} className="absolute left-0 top-[calc(100%+10px)] z-50 flex min-w-48 flex-col gap-0.5 rounded-[14px] border border-border bg-popover p-1.5 text-popover-foreground shadow-[0_24px_48px_-20px_rgba(0,0,0,0.45)]">
          {items.map((i) => (
            <Link
              key={i.href}
              href={i.href}
              role="menuitem"
              aria-current={location.startsWith(i.href) ? "page" : undefined}
              className={cn("flex min-h-10 items-center rounded-[10px] px-2.5 text-sm hover:bg-muted focus-visible:bg-muted focus-visible:outline-none", location.startsWith(i.href) && "font-semibold")}
            >
              {i.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function Header({ me }: { me: Me | null }) {
  const t = useShellT();
  const tt = useTradeT();
  const tm = useMoneyT();
  const tc = useCoachT();
  const ts = useSupportT();
  const tw = useWalletT();
  const setMe = useSetMe();
  const [, navigate] = useLocation();
  const logout = useAction(async () => {
    await post("/auth/logout");
    setMe(null);
    navigate("/login", { replace: true });
  });
  return (
    <header className="sticky top-0 z-header flex h-[calc(3.5rem+env(safe-area-inset-top))] items-center justify-between gap-3 border-b border-border bg-background/90 px-4 pt-[env(safe-area-inset-top)] backdrop-blur">
      <div className="flex min-w-0 items-center gap-3">
        <Link href="/" className="flex shrink-0 items-center" aria-label={t("brand")}>
          <Logo size={28} compact />
        </Link>
        <nav className="hidden min-w-0 items-center gap-0.5 rounded-full bg-card p-1 xl:flex">
          {me ? (
            <>
              <NavLink href="/accounts">{tt("nav.accounts")}</NavLink>
              <NavLink href="/terminal">{tt("nav.terminal")}</NavLink>
              <NavLink href="/programs">{tt("nav.programs")}</NavLink>
              <NavLink href="/payouts">{tm("nav.payouts")}</NavLink>
              <NavLink href="/support">{ts("nav")}</NavLink>
              <NavMore
                label={tt("common.more")}
                items={[
                  { href: "/coach", label: tc("nav") },
                  { href: "/orders", label: tm("nav.orders") },
                  { href: "/balance", label: tw("nav") },
                  { href: "/settings", label: tt("nav.settings") },
                  { href: "/security", label: t("nav.security") },
                ]}
              />
              {me.staff && <NavLink href="/admin">{t("nav.admin")}</NavLink>}
            </>
          ) : (
            <>
              <NavLink href="/programs">{tt("nav.programs")}</NavLink>
              <NavLink href="/faq">{tt("nav.faq")}</NavLink>
            </>
          )}
        </nav>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {can(me, "trading.use.own") && <BalanceChip />}
        <LangMenu label={t("language")} />
        <div className="hidden sm:block">
          <ThemeSwitch />
        </div>
        {me ? (
          <Button type="button" size="sm" variant="ghost" disabled={logout.pending} onClick={() => void logout.run()}>
            {t("nav.signOut")}
          </Button>
        ) : (
          <Button asChild size="sm" variant="outline">
            <Link href="/login">{tt("nav.signIn")}</Link>
          </Button>
        )}
      </div>
    </header>
  );
}

function PhoneNav({ me }: { me: Me | null }) {
  const t = useShellT();
  const tt = useTradeT();
  const tm = useMoneyT();
  const tc = useCoachT();
  const ts = useSupportT();
  const tw = useWalletT();
  const setMe = useSetMe();
  const [, navigate] = useLocation();
  const logout = useAction(async () => {
    await post("/auth/logout");
    setMe(null);
    navigate("/login", { replace: true });
  });
  const more = me
    ? [
        { href: "/coach", label: tc("nav") },
        { href: "/orders", label: tm("nav.orders") },
        { href: "/balance", label: tw("nav") },
        { href: "/settings", label: tt("nav.settings") },
        { href: "/support", label: ts("nav") },
        { href: "/security", label: t("nav.security") },
        ...(me.staff ? [{ href: "/admin", label: t("nav.admin") }] : []),
      ]
    : [];
  return (
    <MobileNav
      me={me}
      labels={{
        accounts: tt("nav.accounts"),
        terminal: tt("nav.terminal"),
        programs: tt("nav.programs"),
        payouts: tm("nav.payouts"),
        faq: tt("nav.faq"),
        signIn: tt("nav.signIn"),
        more: tt("common.more"),
        close: tt("common.close"),
        menu: t("nav.menu"),
      }}
      more={more}
      extra={
        <>
          <ThemeSwitch />
          {me && (
            <Button type="button" variant="outline" disabled={logout.pending} onClick={() => void logout.run()}>
              {t("nav.signOut")}
            </Button>
          )}
        </>
      }
    />
  );
}

/** Signed-in start page: the second-factor reminder (when missing) above the accounts. */
function Home({ me }: { me: Me }) {
  const t = useShellT();
  return (
    <>
      {!me.mfa.enabled && (
        <div className="mx-auto mt-3 w-full max-w-5xl px-4">
          <div className="edge-glow-hover flex flex-wrap items-center gap-3 rounded-[14px] bg-card px-4 py-3">
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[#1558c9]/15 text-[#1558c9] dark:text-[#6f9dff]">
              <ShieldCheck className="size-4.5" aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold">{t("home.secureTitle")}</h2>
              <p className="text-sm text-muted-foreground">{t("home.secureBody")}</p>
            </div>
            <Button asChild size="sm">
              <Link href="/security">{t("home.secureAction")}</Link>
            </Button>
          </div>
        </div>
      )}
      <AccountsPage />
    </>
  );
}

/** While a page's code loads: a quiet line of the page's height, so the footer of the shell does not jump. */
function PageLoading() {
  const t = useShellT();
  return (
    <div className="flex min-h-[60dvh] items-center justify-center text-sm text-muted-foreground" role="status">
      {t("loading")}
    </div>
  );
}

/** An unknown path: one heading and the way back to the public pages. */
function NotFound() {
  const t = useShellT();
  const tt = useTradeT();
  return (
    <main className="mx-auto flex max-w-xl flex-col items-center gap-5 px-4 py-16 text-center">
      <h1 className="text-2xl">{t("notFound")}</h1>
      <nav className="flex flex-wrap justify-center gap-2 text-sm">
        <Link href="/" className="rounded-full border border-border px-3.5 py-1.5 hover:bg-muted">
          {t("brand")}
        </Link>
        <Link href="/programs" className="rounded-full border border-border px-3.5 py-1.5 hover:bg-muted">
          {tt("nav.programs")}
        </Link>
        <Link href="/faq" className="rounded-full border border-border px-3.5 py-1.5 hover:bg-muted">
          {tt("nav.faq")}
        </Link>
      </nav>
    </main>
  );
}

/** The app around every page but the landing: header, phone navigation, the breach screen and the routes. */
export function Shell({ user }: { user: Me | null }) {
  const [location] = useLocation();
  return (
    <StepUpProvider>
      <div className="min-h-dvh bg-background text-foreground">
        <Header me={user} />
        <PhoneNav me={user} />
        {user && (
          <Suspense fallback={null}>
            <BreachScreen />
          </Suspense>
        )}
        {/* Below 1280px the bottom tab bar replaces the header's nav (nine items do not fit a tablet header) and covers
            the last 56px (+ the home indicator): keep content clear of it. */}
        <div key={location} className="fade-in-quick pb-[calc(4.5rem+env(safe-area-inset-bottom)+var(--consent-h,0px))] xl:pb-[var(--consent-h,0px)]">
        <Suspense fallback={<PageLoading />}>
        <Switch>
          <Route path="/verify-email">
            <VerifyEmailPage me={user} />
          </Route>
          <Route path="/reset-password" component={ResetPasswordPage} />
          {!user && <Route path="/login" component={LoginPage} />}
          {!user && <Route path="/register" component={RegisterPage} />}
          {!user && <Route path="/mfa" component={MfaPage} />}
          {!user && <Route path="/forgot-password" component={ForgotPasswordPage} />}
          <Route path="/programs" component={ProgramsPage} />
          <Route path="/programs/:slug" component={RulesPage} />
          <Route path="/faq" component={FaqPage} />
          <Route path="/legal/:doc" component={LegalPage} />
          {/* A guest on a cabinet page signs in first; any other unknown path is "page not found" (a 404 from Caddy). */}
          {!user && needsSignIn(location) && (
            <Route>
              <Redirect to="/login" replace />
            </Route>
          )}
          {user && <Route path="/accounts">{() => <Home me={user} />}</Route>}
          {user && <Route path="/accounts/:id" component={AccountDashboard} />}
          {user && <Route path="/terminal" component={TerminalPage} />}
          {user && <Route path="/terminal/:accountId" component={TerminalPage} />}
          {user && <Route path="/certificate/:id/:phase" component={CertificatePage} />}
          {user && <Route path="/settings" component={SettingsPage} />}
          {user && <Route path="/coach" component={CoachPage} />}
          {user && <Route path="/support" component={SupportPage} />}
          {user && <Route path="/support/:id" component={SupportPage} />}
          {user && <Route path="/buy" component={BuyPage} />}
          {user && <Route path="/checkout/:id" component={CheckoutPage} />}
          {user && <Route path="/orders" component={OrdersPage} />}
          {user && <Route path="/kyc" component={KycPage} />}
          {user && <Route path="/payouts" component={PayoutsPage} />}
          {user && <Route path="/balance" component={BalancePage} />}
          {user && <Route path="/affiliate" component={AffiliatePage} />}
          {user && <Route path="/security">{() => <SecurityPage me={user} />}</Route>}
          {user?.staff && <Route path="/admin">{() => <AdminPage me={user} />}</Route>}
          {user?.staff && <Route path="/admin/:tab">{() => <AdminPage me={user} />}</Route>}
          {/* Signed in already: the sign-in, sign-up and recovery pages lead to the cabinet, not to "page not found". */}
          {user &&
            ["/login", "/register", "/mfa", "/forgot-password"].map((p) => (
              <Route key={p} path={p}>
                <Redirect to="/accounts" replace />
              </Route>
            ))}
          <Route>
            <NotFound />
          </Route>
        </Switch>
        </Suspense>
        </div>
      </div>
    </StepUpProvider>
  );
}
